import { ApolloError } from "@apollo/client";
import { ErrorCode } from "@ticketingplatform/api/dist/const/enums";
import {
  useCreateSetMapMutation,
  useSetMapListQuery,
} from "@ticketingplatform/api/dist/seatMap";
import { GET_SEAT_MAPS } from "@ticketingplatform/api/dist/seatMap/queries";
import {
  QueryMode,
  SortDirection,
} from "@ticketingplatform/api/dist/__generated__/globalTypes";
import { Form, message } from "antd";
import { messagesContent } from "const/messages/formErrors";
import { useAutoComplete } from "hooks/useAutoComplete";
import { useDebouncedValue } from "hooks/useDebouncedValue";
import useFormErrors from "hooks/useFormErrors";
import { useCallback, useState } from "react";
import { useIntl } from "react-intl";
import { handleFormErrors } from "utils/handleFormErrors";
import useImageUpload from "hooks/useImageUpload";
import { useVenueContext } from "contexts/VenueContext";
import { UseAutoCompleteReturnValue } from "hooks/useAutoComplete/types";
import { SeatMap } from "@ticketingplatform/api/dist/seatMap/__generated__/SeatMap";

import {
  SeatMapFromValues,
  UseSeatMapReturnType,
  UseSeatMapSelect,
} from "./types";

export const useSeatMap = (
  onChange: UseAutoCompleteReturnValue<SeatMap>["onChange"]
): UseSeatMapReturnType => {
  const { venue } = useVenueContext();
  const [isSeatMapModalOpen, setIsSeatMapModalOpen] = useState(false);
  const [createSeatMap, { loading }] = useCreateSetMapMutation({
    evictQueries: [GET_SEAT_MAPS],
  });
  const intl = useIntl();
  const [seatMapForm] = Form.useForm();
  const { setApolloError, onValuesChange, showErrorMessage } =
    useFormErrors(seatMapForm);
  const { uploadImage, isUploadLoading } = useImageUpload();

  const onSeatMapFormSubmit = useCallback(
    async (values: SeatMapFromValues) => {
      const {
        input: { image, ...input },
      } = values;

      try {
        const imageId = await (async () => {
          if (!image) {
            return undefined;
          }

          const {
            image: { id },
          } = await uploadImage(image);

          return id;
        })();

        const { data } = await createSeatMap({
          variables: {
            input: { name: input.name, imageId, venueId: venue?.id },
          },
        });

        if (!data?.seatMapCreate) {
          return;
        }

        onChange?.(null, data.seatMapCreate);
        message.success(
          intl.formatMessage({
            defaultMessage: "Seat Map has been added!",
          })
        );
        setIsSeatMapModalOpen(false);
      } catch (error) {
        if (!(error instanceof Error)) {
          throw error;
        }

        if (!(error instanceof ApolloError)) {
          return;
        }

        message.error(
          intl.formatMessage({
            defaultMessage: "Cannot add Seat Map! Check form fields.",
          })
        );
        setApolloError(error);

        const errorMessages = handleFormErrors(error, [
          ErrorCode.INTERNAL_ERROR,
          ErrorCode.ENTITY_EXISTS,
        ]);

        showErrorMessage(errorMessages, input.name, messagesContent);
      }
    },
    [
      createSeatMap,
      intl,
      onChange,
      setApolloError,
      showErrorMessage,
      uploadImage,
      venue?.id,
    ]
  );

  return {
    isSeatMapLoading: loading || isUploadLoading,
    onSeatMapFormSubmit,
    setIsSeatMapModalOpen,
    isSeatMapModalOpen,
    seatMapForm,
    onSeatMapValuesChange: onValuesChange.onFormValuesChange,
  };
};

export const useSeatMapSelect: UseSeatMapSelect = (props) => {
  const { venue } = useVenueContext();
  const [open, onDropdownVisibleChange] = useState(false);
  const [searchValue, debouncedSearch, onSearch] = useDebouncedValue<
    string | undefined
  >(undefined, 1000);

  const { data, loading } = useSetMapListQuery({
    skip: !open,
    variables: {
      orderBy: [{ name: SortDirection.asc }],
      take: 10,
      filters: {
        ...(venue
          ? {
              venueId: {
                equals: venue.id,
              },
            }
          : undefined),
        ...(debouncedSearch
          ? {
              name: {
                contains: debouncedSearch,
                mode: QueryMode.insensitive,
              },
            }
          : undefined),
      },
    },
  });

  return useAutoComplete({
    ...props,
    loading,
    open,
    searchValue,
    onDropdownVisibleChange,
    onSearch,
    options: data?.seatMaps?.nodes || [],
    valueProp: "id",
    labelProp: "name",
  });
};
